import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFindProviderRequired } from 'src/components/Organization/api/hooks/v1/providerHooks';
import { connect } from 'react-redux';
import { flow, get, isEmpty } from 'lodash';
import { LeftNavigationBar, DashboardNavigationLink } from '@unite-us/app-components';
import { withRouter } from 'react-router';
import FeatureFlagContainer from 'src/common/utils/FeatureFlags/FeatureFlagContainer';
import { hasInvoicesAccess, hasPayerInvoicesRole, useInvoiceDisputeWorkflow } from 'src/common/utils/FeatureFlags/flags';
import { CurrentEmployeeContext } from 'src/common/contexts/CurrentEmployeeContext';
import { browserHistory } from 'src/common/utils/browserHistory';
import { CurrentProviderContextProvider } from 'common/contexts/CurrentProviderContext';
import { useInvoiceStatusCounts } from './hooks';

const InvoicesContainer = ({
  children,
  currentEmployee,
  showPayerInvoices,
  providerId,
  showInvoices,
  payerId,
  showInvoiceDisputeWorkflow,
  router,
}) => {
  const { data } = useFindProviderRequired({ providerId });
  const isNetworkLead = get(data, 'provider_type') === 'network_lead';

  useEffect(() => {
    if (!showInvoices) {
      browserHistory.push('/');
    }
  }, [showInvoices]);

  const counts = useInvoiceStatusCounts(providerId, isNetworkLead, payerId, showPayerInvoices, !isEmpty(data));
  const isRouteActive = (path) => Boolean(router && router.isActive({ pathname: path }));

  const navItems = [
    {
      key: 'draft',
      to: '/invoices/draft',
      label: 'Draft',
      count: counts.draft,
      'data-testid': 'draft-invoice-bucket',
      show: !isNetworkLead && !showPayerInvoices,
    },
    {
      key: 'rejected',
      to: '/invoices/rejected',
      label: 'Rejected',
      count: counts.rejectedDWQ,
      'data-testid': 'rejected-invoice-bucket',
      show: !isNetworkLead && !showPayerInvoices,
    },
    {
      key: 'open',
      to: '/invoices/open',
      label: 'Open',
      count: counts.open,
      'data-testid': 'open-invoice-bucket',
      show: isNetworkLead || showPayerInvoices,
    },
    {
      key: 'disputed',
      to: '/invoices/disputed',
      label: 'Disputed',
      count: counts.disputed,
      'data-testid': 'disputed-invoice-bucket',
      show: showInvoiceDisputeWorkflow,
    },
    {
      key: 'submitted',
      to: '/invoices/submitted',
      label: 'Submitted',
      'data-testid': 'submitted-invoice-bucket',
      show: !isNetworkLead && !showPayerInvoices,
    },
    {
      key: 'accepted',
      to: '/invoices/accepted',
      label: 'Accepted',
      'data-testid': 'accepted-invoice-bucket',
      show: !isNetworkLead && !showPayerInvoices,
    },
    {
      key: 'closed',
      to: '/invoices/closed',
      label: 'Closed',
      'data-testid': 'closed-invoice-bucket',
      show: isNetworkLead || showPayerInvoices,
    },
    {
      key: 'archived-invoices',
      to: '/invoices/archive',
      label: 'Archived',
      'data-testid': 'archived-invoice-bucket',
      show: true,
    },
    {
      key: 'all-invoices',
      to: '/invoices/all',
      label: 'All Invoices',
      'data-testid': 'all-invoices-bucket',
      show: true,
    },
  ];

  return (
    showInvoices && (
      <CurrentProviderContextProvider providerId={providerId}>
        <CurrentEmployeeContext.Provider value={currentEmployee}>
          <div className="flex antialiased min-h-full -mx-container-padding">
            <LeftNavigationBar className="space-y-2">
              <>
                {navItems.filter((item) => item.show).map((item) => (
                  <DashboardNavigationLink
                    key={item.key}
                    to={item.to}
                    label={item.label}
                    count={item.count}
                    className="text-13px"
                    data-testid={item['data-testid']}
                    isActive={isRouteActive(item.to)}
                  />
                ))}
              </>
            </LeftNavigationBar>
            <div className="mb-20 break-normal flex-grow overflow-hidden">
              {children}
            </div>
          </div>
        </CurrentEmployeeContext.Provider>
      </CurrentProviderContextProvider>
    )
  );
};

InvoicesContainer.propTypes = {
  children: PropTypes.node.isRequired,
  currentEmployee: PropTypes.object,
  showPayerInvoices: PropTypes.bool,
  providerId: PropTypes.string,
  showInvoices: PropTypes.bool,
  payerId: PropTypes.string,
  showInvoiceDisputeWorkflow: PropTypes.bool,
  router: PropTypes.shape({
    isActive: PropTypes.func,
  }).isRequired,
};

InvoicesContainer.defaultProps = {
  currentEmployee: {},
  providerId: '',
  showInvoices: false,
  showPayerInvoices: false,
  payerId: '',
  showInvoiceDisputeWorkflow: false,
};

function mapStateToProps(state) {
  const providerType = get(state, 'session.currentProvider.provider_type');
  const payerId = providerType === 'payer' ? get(state, 'session.currentProvider.payer.id') : '';

  return {
    payerId,
    currentEmployee: get(state, 'globalState.currentEmployee', ''),
    providerId: get(state, 'session.currentProvider.id', ''),
    showInvoices: hasInvoicesAccess(state),
    showPayerInvoices: hasPayerInvoicesRole(state),
    showInvoiceDisputeWorkflow: useInvoiceDisputeWorkflow(state),
  };
}

export default flow(
  FeatureFlagContainer,
  connect(mapStateToProps),
  withRouter,
)(InvoicesContainer);
